import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Typography } from "@mui/material";
import { type FC, type MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useCanModifyUsers } from "../../hooks/useCanModifyUsers";
import type { SilentJobIdsByNurseId } from "../../hooks/useDepartmentSilentJobs";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { useRoleColors } from "../../hooks/useRoleColors";
import { useTeamRoster } from "../../hooks/useTeamRoster";
import { INK } from "../../theme/tokens";
import { formatEmployeeStatusReason } from "../../utils/employeeStatusReason";
import {
	formatPersonFullName,
	getNurseDepartmentStatus,
	getNurseEffectiveStatus,
	getNurseEmployeeIds,
	getNurseStatusReason,
	isNurseActiveInDepartment,
} from "../../utils/formatters";
import { getPrimaryJobLabel } from "../../utils/jobs";
import { EffectiveEmployeeStatus, EmploymentType, type TeamNurse } from "../../utils/models";
import type { MultiSortOption } from "../../utils/multiColumnSorting";
import { compareValues, type SortingState } from "../../utils/multiColumnSorting";
import { getShortRole, normalizeUnionName } from "../../utils/utilFunctions";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { AlertActionBadge } from "../UIToolkit/Badges/AlertActionBadge";
import EmployeeStatusChip from "../UIToolkit/Badges/EmployeeStatusChip";
import type { SystemGridColDef } from "../UIToolkit/Tables/SystemDataGrid/types";
import { TEAM_SECTIONS } from "./config";
import { NurseNameBadges } from "./NurseNameBadges";
import { useWelcomeSendHistory } from "./ResendWelcome/useWelcomeSendHistory";
import { getReasonTitle } from "./statusDecisions/copy";
import type { OpenStatusDecision } from "./statusDecisions/types";
import { TeamJobCell } from "./TeamJobCell";
import { TEAM_EMPLOYEE_ID_FIELD } from "./teamColumnVisibility";

/** Class the grid's sx uses to reveal a row action only while its row is hovered or focused. */
export const TEAM_ROW_ACTION_CLASS = "team-row-action";

/** The Name column's grid `field` — a row is keyed off the first name but renders the full name. */
export const TEAM_NAME_FIELD = "firstName";

/**
 * Column `field` → the filter-rail group that filters the same thing.
 *
 * Drives the column menu's "Filter by…" hand-off. Name is absent on purpose and handled separately:
 * it has no rail group because search is what filters a name, so its menu item hands off to the
 * search field instead. Started and Employee ID get no item at all.
 *
 * Keyed off `Nurse` rather than `string` so a column `field` that is renamed out from under this map
 * fails to compile. Renaming the Status column's field to `departmentEmployeeStatuses` while this
 * still said `active` silently dropped that column's "Filter by…" item, because a stale key is a
 * lookup miss rather than an error.
 */
const COLUMN_TO_FILTER_GROUP: Readonly<Partial<Record<keyof TeamNurse, string>>> = {
	role: "roles",
	jobs: "jobs",
	employmentType: "contract",
	lastLoginTime: "last_login",
	departmentEmployeeStatuses: "status",
	agencyName: "agency",
	union: "union",
};

/**
 * The filter-rail group a column hands off to, or `undefined` for columns that offer no hand-off.
 *
 * The map is keyed by `keyof Nurse` while the grid hands us a plain `string` field, so the widening
 * cast lives here once rather than at each of the page's three call sites.
 */
export function getColumnFilterGroup(field: string): string | undefined {
	return COLUMN_TO_FILTER_GROUP[field as keyof TeamNurse];
}

function getContractTypeLabel(employmentType: EmploymentType): string {
	return TEAM_SECTIONS.find((s) => s.employmentTypes.includes(employmentType))?.title ?? employmentType;
}

/**
 * Sort key for Last login: the login instant, with never-logged-in pinned BELOW every real date.
 *
 * So ascending reads stalest-first and puts `Never` at the top. That inversion is the whole point —
 * treated as a plain nullable date, `Never` lands wherever the generic comparator drops a null
 * (which was the bottom), and answering "who has gone quiet" took three clicks and still buried the
 * staff who never logged in at all.
 */
function lastLoginRecency(nurse: TeamNurse): number {
	return nurse.lastLoginTime ? nurse.lastLoginTime.toMillis() : Number.NEGATIVE_INFINITY;
}

const TeamNameCell: FC<{ nurse: TeamNurse; isActive: boolean; isDemo: boolean; isSupport: boolean; lastNameFirst: boolean }> = ({
	nurse,
	isActive,
	isDemo,
	isSupport,
	lastNameFirst,
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: isActive ? INK.PRIMARY : "primary.light", fontWeight: 500 }}>
		<Typography noWrap sx={{ color: isActive ? "primary.dark" : "primary.light" }}>
			{formatPersonFullName(nurse, lastNameFirst)}
		</Typography>
		<NurseNameBadges nurse={nurse} isDemo={isDemo} isSupport={isSupport} />
	</Box>
);

const jobsFirstLabel = (n: TeamNurse): string => getPrimaryJobLabel(n) ?? "";

/**
 * Column configuration for the Team page DataGrid.
 *
 * Reliability Score column + sort option are only emitted on demo facilities, matching
 * the prior table's visibility rule (`isSnfDemo || isHospitalDemo`).
 *
 * The Job column relies on `customSortFunction` because `nurse.jobs` is an array — the
 * generic per-column `compareValues` walk can't order arrays. The previous implementation
 * special-cased this in `Team.tsx`; we now push that logic into the sort framework so
 * the grid can own sort end-to-end.
 */
export function useTeamGridColumns({
	departmentId,
	onMenuClick,
	decisionsByNurseId,
	onDecisionClick,
	silentJobIdsByNurseId,
}: {
	departmentId: string;
	onMenuClick: (event: MouseEvent<HTMLButtonElement>, nurse: TeamNurse) => void;
	decisionsByNurseId?: Map<number, OpenStatusDecision>;
	onDecisionClick?: (decision: OpenStatusDecision) => void;
	/** Each employee's silent jobs, for the crossed-out bell in the Job column. */
	silentJobIdsByNurseId?: SilentJobIdsByNurseId;
}): { columns: SystemGridColDef<TeamNurse>[]; sortingOptions: MultiSortOption<TeamNurse>[]; defaultSortState: SortingState<TeamNurse>[] } {
	const { t } = useTranslation();
	const roleColor = useRoleColors();
	const { isAnyDemo, isRealDemo, isSnfDemo, isHospitalDemo } = useHospitalData();
	const { roster } = useTeamRoster(departmentId);
	const showEngagement = isAnyDemo || isRealDemo;
	const { userInfo } = useAuth();
	const isSupport = !!userInfo?.support;
	const lastNameFirst = useLastNameFirst();
	const showReliability = isSnfDemo || isHospitalDemo || isRealDemo;
	// Derived once per roster+department instead of inside `renderCell` AND both operands of every
	// comparator call, which made sorting a large roster (Pharos has ~840 staff) repeat the same
	// filter/dedupe O(n log n) times.
	const employeeIdsByNurseId = useMemo(() => {
		const byId = new Map<number, string[]>();
		for (const nurse of roster) byId.set(nurse.id, getNurseEmployeeIds(nurse, departmentId));
		return byId;
	}, [roster, departmentId]);

	// APP-271: data-driven rather than customer-gated. A department with no HRIS-linked employees has
	// no EE IDs to show, and the column would be a full row of "--" competing for width in a grid that
	// already scrolls horizontally.
	const showEmployeeId = useMemo(() => [...employeeIdsByNurseId.values()].some((ids) => ids.length > 0), [employeeIdsByNurseId]);
	// Only admins who can modify staff are allowed to read this, so asking as anyone else is a
	// guaranteed 403 on every visit to the page.
	const canModifyUsers = useCanModifyUsers();
	const { invitedTodayByNurseId } = useWelcomeSendHistory({ enabled: canModifyUsers });

	return useMemo(() => {
		const columns: SystemGridColDef<TeamNurse>[] = [
			{
				field: TEAM_NAME_FIELD,
				headerName: t("team.column.name"),
				flex: 16,
				// Not hideable: the name is how a row is identified and opened, so a roster without it
				// is unusable rather than merely narrower.
				excludeFromColumnPicker: true,
				renderCell: ({ row }) => (
					<TeamNameCell
						nurse={row}
						isActive={isNurseActiveInDepartment(row, departmentId)}
						isDemo={showEngagement}
						isSupport={isSupport}
						lastNameFirst={lastNameFirst}
					/>
				),
			},
			{
				field: "role",
				headerName: t("team.column.role"),
				flex: 12,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: roleColor(row.role).dot }} />
							<Typography noWrap sx={{ color: isActive ? "primary.dark" : "primary.light", fontWeight: 600 }}>
								{getShortRole(row.role)}
							</Typography>
						</Box>
					);
				},
			},
			{
				field: "jobs",
				headerName: t("team.column.job"),
				flex: 14,
				renderCell: ({ row }) => (
					<TeamJobCell nurse={row} isActive={isNurseActiveInDepartment(row, departmentId)} silentJobIds={silentJobIdsByNurseId?.get(row.id)} />
				),
			},
			{
				field: "employmentType",
				headerName: t("team.column.contract_type"),
				flex: 14,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					return (
						<Box sx={{ color: isActive ? "primary.dark" : "primary.light", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
							{getContractTypeLabel(row.employmentType)}
							{row.agencyName && (row.employmentType === EmploymentType.Travel || row.employmentType === EmploymentType.PerDiem) && (
								<span style={{ marginLeft: "4px", opacity: 0.7 }}>({row.agencyName})</span>
							)}
						</Box>
					);
				},
			},
			{
				field: "nursingStartingDate",
				headerName: t("team.column.start_date"),
				flex: 12,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					return (
						<Typography noWrap sx={{ color: isActive ? "#4D5579" : "#A0A0A0", fontSize: "14px" }}>
							{row.nursingStartingDate?.toFormat("MMM d, yyyy") || "--"}
						</Typography>
					);
				},
			},
			{
				field: "lastLoginTime",
				headerName: t("team.column.last_login"),
				flex: 12,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					// Relative, because the question is how long ago — nobody scanning 300 rows is
					// subtracting dates. `Never` takes the same muted treatment as any empty value: no
					// color coding, since a nurse with no app account looks identical here to one who
					// simply ignores it, and red would imply a diagnosis the roster can't make.
					if (!row.lastLoginTime) {
						// A welcome that went out today is the most recent thing known about this person, and
						// says "we have already chased them" where a bare "Never" invites chasing them again.
						const invitedAt = invitedTodayByNurseId.get(row.id);
						if (invitedAt) {
							return (
								<Typography noWrap sx={{ color: "primary.dark", fontSize: "14px" }} data-testid={`team-invite-sent-${row.id}`}>
									{t("team.cell.invite_sent", { date: invitedAt.toFormat("MMM d") })}
								</Typography>
							);
						}
						return <Typography sx={{ color: INK.DISABLED, fontSize: "14px" }}>{t("team.cell.never")}</Typography>;
					}
					return (
						<InHouseTooltip title={row.lastLoginTime.toFormat("ccc, MMM d, yyyy HH:mm")}>
							<Typography noWrap sx={{ color: isActive ? "primary.dark" : "primary.light", fontSize: "14px" }}>
								{row.lastLoginTime.toRelative()}
							</Typography>
						</InHouseTooltip>
					);
				},
			},
			{
				// Not a `Nurse` property any more — status is per-department and rendered via
				// getNurseDepartmentStatus below. Kept as a stable column id, so `sortable: false` stops
				// MUI's own header sort falling back to `row[field]`, which would now be undefined.
				// Sorting is offered through the sortingOptions list instead, like the Reason column.
				field: "departmentEmployeeStatuses",
				headerName: t("team.column.status"),
				flex: 11,
				sortable: false,
				renderCell: ({ row }) => {
					const { status, reason, endDate } = getNurseDepartmentStatus(row, departmentId);
					return (
						<EmployeeStatusChip status={status} reason={formatEmployeeStatusReason(reason, t)} endDate={endDate} dataTestId={`team-status-chip-${row.id}`} />
					);
				},
			},
		];

		if (showEmployeeId) {
			columns.push({
				field: TEAM_EMPLOYEE_ID_FIELD,
				headerName: t("team.column.employee_id"),
				flex: 12,
				// An ellipsized id is useless to someone reconciling a list against payroll, and at the
				// grid's flex ratios this column collapsed to "demo-..." on a 1280px viewport. Ids run
				// 2-36 characters (avg 7.3), so this floor shows the common case whole and lets the
				// tooltip carry the long tail.
				minWidth: 120,
				sortable: false,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					// Rows always come from the same roster the map was built from; recompute rather than
					// defaulting to empty so an unexpected miss shows the real value instead of a blank.
					const employeeIds = employeeIdsByNurseId.get(row.id) ?? getNurseEmployeeIds(row, departmentId);
					// Several ids means several HRIS records for one person in this department — worth
					// seeing during an audit, so show them all and let the tooltip carry the overflow.
					const label = employeeIds.join(", ");
					return (
						<Typography
							variant="body2"
							noWrap
							// Always, not just for the multi-id case: a single long id ellipsizes too, and then
							// hovering is the only way to read the number the audit is about.
							title={label || undefined}
							sx={{ color: isActive ? "primary.dark" : "primary.light", fontSize: "14px" }}
							data-testid={`team-employee-id-${row.id}`}
						>
							{label || "--"}
						</Typography>
					);
				},
			});
		}

		if (showReliability) {
			columns.push({
				field: "reliabilityScore",
				headerName: t("team.column.reliability_score"),
				flex: 10,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					return (
						<Typography noWrap sx={{ color: isActive ? "primary.dark" : "primary.light", fontSize: "14px" }}>
							{row.reliabilityScore}
						</Typography>
					);
				},
			});
		}

		// ── Off by default: available from the Columns picker ──
		// These are the fields people occasionally audit rather than scan. Status reason in particular
		// renders blank for every ACTIVE employee by design, so on the default Active-only roster it
		// was a full column of "--" holding width that the Name column needed.
		columns.push(
			{
				field: "departmentStatusDetails",
				headerName: t("employee.status.column.reason"),
				flex: 14,
				sortable: false,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					// Active nurses have no live status window — never surface a stale reason for them.
					const effectiveStatus = getNurseEffectiveStatus(row, departmentId);
					const reason = effectiveStatus === EffectiveEmployeeStatus.Active ? null : formatEmployeeStatusReason(getNurseStatusReason(row, departmentId), t);
					return (
						<Typography variant="body2" sx={{ color: isActive ? "primary.dark" : "primary.light" }} noWrap title={reason ?? undefined}>
							{reason || "--"}
						</Typography>
					);
				},
			},
			{
				field: "email",
				headerName: t("team.column.email"),
				flex: 16,
				renderCell: ({ row }) => (
					<Typography variant="body2" noWrap title={row.email ?? undefined} sx={{ color: "primary.dark", fontSize: "14px" }}>
						{row.email || "--"}
					</Typography>
				),
			},
			{
				field: "phoneNumber",
				headerName: t("team.column.phone"),
				flex: 12,
				renderCell: ({ row }) => (
					<Typography variant="body2" noWrap sx={{ color: "primary.dark", fontSize: "14px" }}>
						{row.phoneNumber || "--"}
					</Typography>
				),
			},
			{
				field: "agencyName",
				headerName: t("team.filter.group.agency"),
				flex: 14,
				renderCell: ({ row }) => (
					<Typography variant="body2" noWrap title={row.agencyName ?? undefined} sx={{ color: "primary.dark", fontSize: "14px" }}>
						{row.agencyName?.trim() || "--"}
					</Typography>
				),
			},
			{
				field: "union",
				headerName: t("team.filter.group.union"),
				flex: 12,
				renderCell: ({ row }) => (
					<Typography variant="body2" noWrap sx={{ color: "primary.dark", fontSize: "14px" }}>
						{normalizeUnionName(row.union) ?? "--"}
					</Typography>
				),
			},
			{
				field: "id",
				headerName: "",
				flex: 5,
				sortable: false,
				disableExport: true,
				// Not hideable: this cell holds the row's only menu (status, deactivate, set password).
				excludeFromColumnPicker: true,
				renderCell: ({ row }) => {
					const isActive = isNurseActiveInDepartment(row, departmentId);
					const decision = decisionsByNurseId?.get(row.id);
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
							{/* The decision badge is a SIGNAL, so it stays visible; only the menu hides until hover. */}
							{decision && onDecisionClick && (
								<AlertActionBadge
									dataTestId={`team-row-status-decision-${row.id}`}
									tooltip={getReasonTitle(t, decision.reason)}
									ariaLabel={t("team.status.decision.badge.aria")}
									onClick={() => onDecisionClick(decision)}
								/>
							)}
							<IconButton
								className={TEAM_ROW_ACTION_CLASS}
								size="small"
								aria-label={t("team.column.actions")}
								data-testid={`team-row-actions-${row.id}`}
								onClick={(event) => {
									event.stopPropagation();
									onMenuClick(event, row);
								}}
								sx={{ color: isActive ? "primary.dark" : "primary.light" }}
							>
								<MoreVertIcon fontSize="small" />
							</IconButton>
						</Box>
					);
				},
			},
		);

		const sortingOptions: MultiSortOption<TeamNurse>[] = [
			{
				label: t("team.column.name"),
				sortColumns: ["firstName", "lastName"],
				// Sorts on the name as displayed, so "Last First" departments read alphabetically by last name.
				customSortFunction: (a, b) => {
					const cmp = compareValues(formatPersonFullName(a, lastNameFirst), formatPersonFullName(b, lastNameFirst));
					return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
				},
			},
			{ label: t("team.column.role"), sortColumns: ["role"] },
			{
				label: t("team.column.job"),
				sortColumns: ["jobs"],
				customSortFunction: (a, b) => {
					const cmp = compareValues(jobsFirstLabel(a), jobsFirstLabel(b));
					return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
				},
			},
			{ label: t("team.column.contract_type"), sortColumns: ["employmentType"] },
			{ label: t("team.column.start_date"), sortColumns: ["nursingStartingDate"] },
			{
				label: t("team.column.last_login"),
				sortColumns: ["lastLoginTime"],
				// Sorts on staleness, not on the raw nullable date — see `lastLoginRecency`.
				customSortFunction: (a, b) => {
					const cmp = compareValues(lastLoginRecency(a), lastLoginRecency(b));
					return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
				},
			},
			...(showEmployeeId
				? [
						{
							label: t("team.column.employee_id"),
							sortColumns: [TEAM_EMPLOYEE_ID_FIELD as keyof TeamNurse],
							// `externalEmployees` is an array, which the generic per-column compareValues walk
							// cannot order — same reason the Job column carries one.
							customSortFunction: (a: TeamNurse, b: TeamNurse) => {
								const idsOf = (n: TeamNurse) => (employeeIdsByNurseId.get(n.id) ?? getNurseEmployeeIds(n, departmentId)).join(", ");
								const cmp = compareValues(idsOf(a), idsOf(b));
								return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
							},
						},
					]
				: []),
			...(showReliability ? [{ label: t("team.column.reliability_score"), sortColumns: ["reliabilityScore" as keyof TeamNurse] }] : []),
			{
				// Sorts on the per-department effective status (Active / Inactive / On Leave / Suspended),
				// which is what the Status column actually renders. It previously keyed on a nurse-level
				// `active` rollup, so an employee active at a sibling facility sorted as Active here.
				label: t("team.column.status"),
				sortColumns: ["departmentEmployeeStatuses"],
				customSortFunction: (a, b) => {
					const cmp = compareValues(getNurseEffectiveStatus(a, departmentId), getNurseEffectiveStatus(b, departmentId));
					return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
				},
			},
			{
				label: t("employee.status.column.reason"),
				sortColumns: ["departmentStatusDetails"],
				customSortFunction: (a, b) => {
					const cmp = compareValues(
						formatEmployeeStatusReason(getNurseStatusReason(a, departmentId), t) ?? "",
						formatEmployeeStatusReason(getNurseStatusReason(b, departmentId), t) ?? "",
					);
					return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
				},
			},
		];

		const defaultSortState: SortingState<TeamNurse>[] = [{ sortColumns: ["firstName", "lastName"], sortOrder: "asc", label: t("team.column.name") }];

		return { columns, sortingOptions, defaultSortState };
	}, [
		departmentId,
		showEngagement,
		isSupport,
		lastNameFirst,
		showReliability,
		showEmployeeId,
		employeeIdsByNurseId,
		invitedTodayByNurseId,
		onMenuClick,
		decisionsByNurseId,
		onDecisionClick,
		roleColor,
		silentJobIdsByNurseId,
		t,
	]);
}
